import React, { useState, useEffect } from 'react';
import { Globe, EyeOff } from 'lucide-react';
import { Modal, Button, TextField, DatePicker } from '@anyemp/ui-kit';
import toast from 'react-hot-toast';
import { talentPosts } from '../../services/apiService';

const PublishModal = ({ isOpen, onClose, post, onPublished }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ link: '', publish_date: today });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && post) {
      setForm({
        link: post.link || '',
        publish_date: post.publish_date ? post.publish_date.slice(0, 10) : today
      });
    }
  }, [isOpen, post]);

  const isPublished = post?.publish_status === 'published';

  const handlePublish = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await talentPosts.publish(post.id, {
        link: form.link.trim() || null,
        publish_date: form.publish_date || null
      });
      toast.success('Post published');
      onPublished();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to publish');
    } finally {
      setSaving(false);
    }
  };

  const handleUnpublish = async () => {
    setSaving(true);
    try {
      await talentPosts.unpublish(post.id);
      toast.success('Post unpublished');
      onPublished();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed to unpublish');
    } finally {
      setSaving(false);
    }
  };

  if (!post) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size="small"
      showCloseButton
      style={{ backgroundColor: 'var(--palette-background-default)' }}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-2 pr-7">
          {isPublished
            ? <EyeOff size={18} className="text-orange-500" />
            : <Globe size={18} className="text-green-500" />
          }
          <h2 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
            {isPublished ? 'Manage Publication' : 'Publish Post'}
          </h2>
        </div>

        <form onSubmit={handlePublish} className="space-y-4">
          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary">
            {isPublished ? 'Published: ' : 'Publishing: '}
            <span className="font-medium text-light-text-primary dark:text-dark-text-primary">{post.name}</span>
          </p>

          <TextField
            label="Link (URL)"
            type="url"
            fullWidth
            value={form.link}
            onChange={e => setForm(prev => ({ ...prev, link: e.target.value }))}
            placeholder="https://linkedin.com/..."
          />

          <DatePicker
            label="Publish Date"
            fullWidth
            value={form.publish_date ? new Date(form.publish_date) : null}
            onChange={d => setForm(prev => ({ ...prev, publish_date: d ? d.toISOString().slice(0, 10) : '' }))}
          />

          <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:flex-wrap sm:justify-between [&>*]:w-full sm:[&>*]:w-auto">
            {isPublished && (
              <Button type="button" color="warning" onClick={handleUnpublish} disabled={saving}>
                {saving ? 'Saving…' : 'Unpublish'}
              </Button>
            )}
            <div className="flex flex-col-reverse gap-3 sm:ml-auto sm:flex-row sm:flex-wrap [&>*]:w-full sm:[&>*]:w-auto">
              <Button type="button" variant="outlined" color="neutral" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" color="success" disabled={saving}>
                {saving ? 'Saving…' : (isPublished ? 'Update' : 'Publish')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default PublishModal;
